<ng-container *ngIf="payload">
  <div class="payload-viewer" [class.compact]="compactMode">

    <!-- Pipeline Header -->
    <div class="pipeline-header">
      <div class="pipeline-title">
        <i class="fa-solid fa-brain"></i>
        <span>AI Processing Pipeline</span>
      </div>
      <div class="pipeline-summary">
        <span class="summary-item" *ngIf="responseSection?.complete">
          <i class="fa-solid fa-check-circle"></i> Response Generated
        </span>
        <span class="summary-item" *ngIf="factCheckSection?.passed">
          <i class="fa-solid fa-shield-check"></i> Verified
        </span>
      </div>
    </div>

    <!-- Pipeline Stages -->
    <div class="pipeline-stages">

      <!-- ==================== STAGE 1: Inbound Message ==================== -->
      <div class="pipeline-stage stage-inbound" [class.collapsed]="!isSectionExpanded('inbound')">
        <div class="stage-connector">
          <div class="connector-line"></div>
          <div class="connector-dot" [ngClass]="getStatusClass(getSectionStatus('inbound'))">
            <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('inbound'))"></i>
          </div>
        </div>

        <div class="stage-content">
          <button class="stage-header" (click)="toggleSection('inbound')">
            <div class="stage-icon">
              <i class="fa-solid fa-envelope"></i>
            </div>
            <div class="stage-title-area">
              <h3>Inbound Message</h3>
              <span class="stage-subtitle" *ngIf="inboundMessage">
                From {{ inboundMessage.senderEmail }}
              </span>
            </div>
            <div class="stage-status" [ngClass]="getStatusClass(getSectionStatus('inbound'))">
              <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('inbound'))"></i>
            </div>
            <i class="fa-solid fa-chevron-down toggle-icon"></i>
          </button>

          <div class="stage-body" *ngIf="isSectionExpanded('inbound') && inboundMessage">
            <div class="inbound-card">
              <div class="inbound-header">
                <div class="sender-avatar">
                  {{ getEmailInitials(inboundMessage.senderEmail) }}
                </div>
                <div class="sender-info">
                  <div class="sender-email">{{ inboundMessage.senderEmail }}</div>
                  <div class="message-subject" *ngIf="inboundMessage.subject">
                    {{ inboundMessage.subject }}
                  </div>
                </div>
              </div>
              <div class="inbound-body">
                <div class="message-preview">
                  {{ truncateText(inboundMessage.body, 300) }}
                </div>
              </div>
              <div class="inbound-meta">
                <span class="meta-item" *ngIf="inboundMessage.channelId">
                  <i class="fa-solid fa-hashtag"></i> {{ inboundMessage.channelId }}
                </span>
                <span class="meta-item" *ngIf="inboundMessage.threadId">
                  <i class="fa-solid fa-link"></i> Thread
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== STAGE 2: Parsed Message ==================== -->
      <div class="pipeline-stage stage-parsed" [class.collapsed]="!isSectionExpanded('parsed')">
        <div class="stage-connector">
          <div class="connector-line"></div>
          <div class="connector-dot" [ngClass]="getStatusClass(getSectionStatus('parsed'))">
            <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('parsed'))"></i>
          </div>
        </div>

        <div class="stage-content">
          <button class="stage-header" (click)="toggleSection('parsed')">
            <div class="stage-icon">
              <i class="fa-solid fa-scissors"></i>
            </div>
            <div class="stage-title-area">
              <h3>Message Parsed</h3>
              <span class="stage-subtitle" *ngIf="parsedMessage?.complete">
                Extracted latest message
              </span>
            </div>
            <div class="stage-status" [ngClass]="getStatusClass(getSectionStatus('parsed'))">
              <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('parsed'))"></i>
            </div>
            <i class="fa-solid fa-chevron-down toggle-icon"></i>
          </button>

          <div class="stage-body" *ngIf="isSectionExpanded('parsed') && parsedMessage">
            <div class="parsed-section">
              <div class="parsed-latest">
                <label><i class="fa-solid fa-quote-left"></i> Latest Message</label>
                <div class="latest-content">
                  {{ parsedMessage.latestMessage }}
                </div>
              </div>

              <div class="reply-chain-section" *ngIf="replyChain.length > 0">
                <label>
                  <i class="fa-solid fa-reply-all"></i>
                  Reply Chain ({{ replyChain.length }} messages)
                </label>
                <div class="reply-chain">
                  <div class="chain-item" *ngFor="let msg of replyChain; let i = index">
                    <div class="chain-marker">{{ replyChain.length - i }}</div>
                    <div class="chain-content">
                      <span class="chain-sender">{{ msg.sender }}</span>
                      <span class="chain-time" *ngIf="msg.timestamp">{{ msg.timestamp }}</span>
                      <p class="chain-text">{{ truncateText(msg.content, 150) }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="signature-note" *ngIf="parsedMessage.signature">
                <i class="fa-solid fa-signature"></i>
                <span>Signature detected and stripped</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== STAGE 3: Early Rejection ==================== -->
      <div class="pipeline-stage stage-filter" [class.collapsed]="!isSectionExpanded('earlyRejection')">
        <div class="stage-connector">
          <div class="connector-line"></div>
          <div class="connector-dot" [ngClass]="getStatusClass(getSectionStatus('earlyRejection'))">
            <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('earlyRejection'))"></i>
          </div>
        </div>

        <div class="stage-content">
          <button class="stage-header" (click)="toggleSection('earlyRejection')">
            <div class="stage-icon">
              <i class="fa-solid fa-shield-halved"></i>
            </div>
            <div class="stage-title-area">
              <h3>Early Filter</h3>
              <span class="stage-subtitle" *ngIf="earlyRejection?.complete">
                {{ earlyRejection?.isObviousGarbage ? 'Blocked as ' + formatGarbageType(earlyRejection?.garbageType) : 'Passed - Legitimate message' }}
              </span>
            </div>
            <div class="stage-status" [ngClass]="getStatusClass(getSectionStatus('earlyRejection'))">
              <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('earlyRejection'))"></i>
            </div>
            <i class="fa-solid fa-chevron-down toggle-icon"></i>
          </button>

          <div class="stage-body" *ngIf="isSectionExpanded('earlyRejection') && earlyRejection">
            <div class="filter-result" [class.blocked]="earlyRejection.isObviousGarbage">
              <div class="result-badge" *ngIf="!earlyRejection.isObviousGarbage">
                <i class="fa-solid fa-circle-check"></i>
                <span>Not Garbage</span>
              </div>
              <div class="result-badge blocked" *ngIf="earlyRejection.isObviousGarbage">
                <i class="fa-solid" [ngClass]="getGarbageTypeIcon(earlyRejection.garbageType)"></i>
                <span>{{ formatGarbageType(earlyRejection.garbageType) }}</span>
              </div>

              <div class="reasoning-box">
                <label><i class="fa-solid fa-lightbulb"></i> Reasoning</label>
                <p>{{ earlyRejection.reasoning }}</p>
              </div>

              <div class="rejection-reason" *ngIf="earlyRejection.isObviousGarbage && earlyRejection.reason">
                <label><i class="fa-solid fa-comment-slash"></i> Reason</label>
                <p>{{ earlyRejection.reason }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== STAGE 4: Conversation Context ==================== -->
      <div class="pipeline-stage stage-context" [class.collapsed]="!isSectionExpanded('conversation')">
        <div class="stage-connector">
          <div class="connector-line"></div>
          <div class="connector-dot" [ngClass]="getStatusClass(getSectionStatus('conversation'))">
            <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('conversation'))"></i>
          </div>
        </div>

        <div class="stage-content">
          <button class="stage-header" (click)="toggleSection('conversation')">
            <div class="stage-icon">
              <i class="fa-solid fa-comments"></i>
            </div>
            <div class="stage-title-area">
              <h3>Conversation Context</h3>
              <span class="stage-subtitle" *ngIf="conversationContext?.complete">
                {{ relevantConversations.length }} related conversation{{ relevantConversations.length !== 1 ? 's' : '' }} found
              </span>
            </div>
            <div class="stage-status" [ngClass]="getStatusClass(getSectionStatus('conversation'))">
              <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('conversation'))"></i>
            </div>
            <i class="fa-solid fa-chevron-down toggle-icon"></i>
          </button>

          <div class="stage-body" *ngIf="isSectionExpanded('conversation') && conversationContext">
            <div class="conversations-list" *ngIf="relevantConversations.length > 0">
              <div class="conversation-card" *ngFor="let conv of relevantConversations"
                   [class.expandable]="hasExpandableContent(conv)"
                   [class.expanded]="isConversationExpanded(conv.conversationId)">
                <div class="conv-header" (click)="hasExpandableContent(conv) ? toggleConversationExpansion(conv.conversationId) : null">
                  <i class="fa-solid fa-clock"></i>
                  <span class="conv-time">{{ formatConversationDate(conv.timestamp) }}</span>
                  <i class="fa-solid fa-chevron-down conv-expand-icon"
                     *ngIf="hasExpandableContent(conv)"
                     [class.expanded]="isConversationExpanded(conv.conversationId)"></i>
                </div>

                <!-- Collapsed view (summary) -->
                <div class="conv-exchange" *ngIf="!isConversationExpanded(conv.conversationId)">
                  <div class="conv-user">
                    <span class="conv-label">User:</span>
                    <span class="conv-text">{{ conv.inboundSummary || truncateText(conv.inboundMessage, 100) }}</span>
                  </div>
                  <div class="conv-izzy">
                    <span class="conv-label">Izzy:</span>
                    <span class="conv-text">{{ truncateText(conv.response, 100) }}</span>
                  </div>
                </div>

                <!-- Expanded view (full details) -->
                <div class="conv-expanded" *ngIf="isConversationExpanded(conv.conversationId)">
                  <div class="conv-detail-section">
                    <label><i class="fa-solid fa-user"></i> User Message</label>
                    <div class="conv-full-text scrollable-content">{{ conv.inboundMessage }}</div>
                  </div>
                  <div class="conv-detail-section">
                    <label><i class="fa-solid fa-robot"></i> Izzy Response</label>
                    <div class="conv-full-text scrollable-content">{{ conv.response }}</div>
                  </div>
                  <div class="conv-detail-row" *ngIf="conv.conversationId">
                    <span class="detail-label">Conversation ID:</span>
                    <code class="detail-value">{{ conv.conversationId }}</code>
                  </div>
                </div>

                <div class="conv-subject" *ngIf="conv.responseSubject">
                  <i class="fa-solid fa-tag"></i> {{ conv.responseSubject }}
                </div>
              </div>
            </div>

            <div class="empty-conversations" *ngIf="relevantConversations.length === 0">
              <i class="fa-solid fa-inbox"></i>
              <span>No related conversations found</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== STAGE 5: User Context ==================== -->
      <div class="pipeline-stage stage-user" [class.collapsed]="!isSectionExpanded('user')">
        <div class="stage-connector">
          <div class="connector-line"></div>
          <div class="connector-dot" [ngClass]="getStatusClass(getSectionStatus('user'))">
            <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('user'))"></i>
          </div>
        </div>

        <div class="stage-content">
          <button class="stage-header" (click)="toggleSection('user')">
            <div class="stage-icon">
              <i class="fa-solid fa-user-circle"></i>
            </div>
            <div class="stage-title-area">
              <h3>User Context</h3>
              <span class="stage-subtitle" *ngIf="userContext?.complete">
                {{ userFacts.length }} fact{{ userFacts.length !== 1 ? 's' : '' }} loaded
              </span>
            </div>
            <div class="stage-status" [ngClass]="getStatusClass(getSectionStatus('user'))">
              <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('user'))"></i>
            </div>
            <i class="fa-solid fa-chevron-down toggle-icon"></i>
          </button>

          <div class="stage-body" *ngIf="isSectionExpanded('user') && userContext">
            <div class="user-context-grid">
              <!-- Profile Card -->
              <div class="profile-card" *ngIf="userContext.profile">
                <div class="profile-header">
                  <div class="profile-avatar">
                    {{ getEmailInitials(userContext.profile.email) }}
                  </div>
                  <div class="profile-info">
                    <div class="profile-name" *ngIf="userContext.profile.name">
                      {{ userContext.profile.name }}
                    </div>
                    <div class="profile-email">{{ userContext.profile.email }}</div>
                    <div class="profile-org" *ngIf="userContext.profile.organization">
                      <i class="fa-solid fa-building"></i> {{ userContext.profile.organization }}
                    </div>
                  </div>
                </div>
                <div class="profile-badges">
                  <span class="profile-badge" *ngIf="userContext.profile.membershipStatus">
                    <i class="fa-solid fa-id-card"></i> {{ userContext.profile.membershipStatus }}
                  </span>
                  <span class="profile-badge" *ngIf="userContext.profile.memberSince">
                    <i class="fa-solid fa-calendar"></i> Since {{ userContext.profile.memberSince | date:'yyyy' }}
                  </span>
                </div>
              </div>

              <!-- Facts Grid -->
              <div class="facts-grid" *ngIf="userFacts.length > 0">
                <div class="facts-category" *ngFor="let entry of getFactsByCategory() | keyvalue">
                  <div class="category-header">
                    <i class="fa-solid" [ngClass]="getCategoryIcon(entry.key)"></i>
                    <span>{{ entry.key }}</span>
                    <span class="category-count">{{ entry.value.length }}</span>
                  </div>
                  <div class="category-facts">
                    <div class="fact-item" *ngFor="let fact of entry.value" [class.expandable]="isExpandableValue(fact.value)">
                      <div class="fact-row" (click)="isExpandableValue(fact.value) ? toggleFactExpansion(entry.key, fact.key) : null">
                        <span class="fact-key">{{ fact.key }}</span>
                        <span class="fact-value">
                          {{ formatFactValue(fact.value) }}
                          <i class="fa-solid fa-chevron-down fact-expand-icon"
                             *ngIf="isExpandableValue(fact.value)"
                             [class.expanded]="isFactExpanded(entry.key, fact.key)"></i>
                        </span>
                      </div>
                      <!-- Expandable object content -->
                      <div class="fact-object-content" *ngIf="isExpandableValue(fact.value) && isFactExpanded(entry.key, fact.key)">
                        <pre class="fact-json">{{ formatObjectValue(fact.value) }}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== STAGE 6: Request Decision ==================== -->
      <div class="pipeline-stage stage-decision" [class.collapsed]="!isSectionExpanded('decision')">
        <div class="stage-connector">
          <div class="connector-line"></div>
          <div class="connector-dot" [ngClass]="getStatusClass(getSectionStatus('decision'))">
            <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('decision'))"></i>
          </div>
        </div>

        <div class="stage-content">
          <button class="stage-header" (click)="toggleSection('decision')">
            <div class="stage-icon">
              <i class="fa-solid fa-gavel"></i>
            </div>
            <div class="stage-title-area">
              <h3>Request Decision</h3>
              <span class="stage-subtitle" *ngIf="requestDecision?.complete">
                {{ requestDecision?.canProcess ? 'Approved to process' : 'Cannot process - ' + formatRejectionCategory(requestDecision?.rejectionCategory) }}
              </span>
            </div>
            <div class="stage-status" [ngClass]="getStatusClass(getSectionStatus('decision'))">
              <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('decision'))"></i>
            </div>
            <i class="fa-solid fa-chevron-down toggle-icon"></i>
          </button>

          <div class="stage-body" *ngIf="isSectionExpanded('decision') && requestDecision">
            <div class="decision-result" [class.rejected]="!requestDecision.canProcess">
              <div class="decision-badge approved" *ngIf="requestDecision.canProcess">
                <i class="fa-solid fa-thumbs-up"></i>
                <span>Approved to Process</span>
              </div>
              <div class="decision-badge rejected" *ngIf="!requestDecision.canProcess">
                <i class="fa-solid" [ngClass]="getRejectionCategoryIcon(requestDecision.rejectionCategory)"></i>
                <span>{{ formatRejectionCategory(requestDecision.rejectionCategory) }}</span>
              </div>

              <div class="reasoning-box">
                <label><i class="fa-solid fa-lightbulb"></i> Reasoning</label>
                <p>{{ requestDecision.reasoning }}</p>
              </div>

              <div class="rejection-reason" *ngIf="!requestDecision.canProcess && requestDecision.whyCantProcess">
                <label><i class="fa-solid fa-hand"></i> Why Can't Process</label>
                <p>{{ requestDecision.whyCantProcess }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== STAGE 7: Knowledge Gathered ==================== -->
      <div class="pipeline-stage stage-knowledge" [class.collapsed]="!isSectionExpanded('knowledge')">
        <div class="stage-connector">
          <div class="connector-line"></div>
          <div class="connector-dot" [ngClass]="getStatusClass(getSectionStatus('knowledge'))">
            <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('knowledge'))"></i>
          </div>
        </div>

        <div class="stage-content">
          <button class="stage-header" (click)="toggleSection('knowledge')">
            <div class="stage-icon">
              <i class="fa-solid fa-book-open"></i>
            </div>
            <div class="stage-title-area">
              <h3>Knowledge Gathered</h3>
              <span class="stage-subtitle" *ngIf="knowledgeSection?.complete">
                {{ knowledgeFacts.length }} fact{{ knowledgeFacts.length !== 1 ? 's' : '' }} retrieved
              </span>
            </div>
            <div class="stage-status" [ngClass]="getStatusClass(getSectionStatus('knowledge'))">
              <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('knowledge'))"></i>
            </div>
            <i class="fa-solid fa-chevron-down toggle-icon"></i>
          </button>

          <div class="stage-body" *ngIf="isSectionExpanded('knowledge') && knowledgeSection">
            <div class="knowledge-facts" *ngIf="knowledgeFacts.length > 0">
              <div class="knowledge-card" *ngFor="let fact of knowledgeFacts">
                <div class="knowledge-inquiry">
                  <i class="fa-solid fa-question-circle"></i>
                  <span>{{ fact.inquiry }}</span>
                </div>
                <div class="knowledge-answer scrollable-content" [innerHTML]="renderMarkdown(fact.text)">
                </div>
                <div class="knowledge-meta">
                  <span class="tool-badge">
                    <i class="fa-solid fa-wrench"></i> {{ fact.toolUsed }}
                  </span>
                  <span class="confidence-badge" *ngIf="fact.confidence !== undefined"
                        [ngClass]="getConfidenceClass(fact.confidence)">
                    <i class="fa-solid fa-bullseye"></i> {{ formatConfidence(fact.confidence) }}
                  </span>
                </div>
                <div class="citations" *ngIf="fact.citations && fact.citations.length > 0">
                  <div class="citation" *ngFor="let citation of fact.citations">
                    <i class="fa-solid fa-link"></i>
                    <ng-container *ngIf="citation.url; else noUrlCitation">
                      <a class="citation-link" [href]="citation.url" target="_blank" rel="noopener noreferrer">
                        {{ citation.source }}
                        <i class="fa-solid fa-external-link-alt citation-external"></i>
                      </a>
                    </ng-container>
                    <ng-template #noUrlCitation>
                      <span class="citation-source">{{ citation.source }}</span>
                    </ng-template>
                    <span class="citation-excerpt" *ngIf="citation.excerpt">
                      "{{ truncateText(citation.excerpt, 80) }}"
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="empty-knowledge" *ngIf="knowledgeFacts.length === 0">
              <i class="fa-solid fa-book"></i>
              <span>No knowledge facts gathered</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== STAGE 8: Response Written ==================== -->
      <div class="pipeline-stage stage-response" [class.collapsed]="!isSectionExpanded('response')">
        <div class="stage-connector">
          <div class="connector-line"></div>
          <div class="connector-dot" [ngClass]="getStatusClass(getSectionStatus('response'))">
            <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('response'))"></i>
          </div>
        </div>

        <div class="stage-content">
          <button class="stage-header" (click)="toggleSection('response')">
            <div class="stage-icon">
              <i class="fa-solid fa-pen-fancy"></i>
            </div>
            <div class="stage-title-area">
              <h3>Response Written</h3>
              <span class="stage-subtitle" *ngIf="responseSection?.complete && responseSection?.subject">
                {{ truncateText(responseSection!.subject, 50) }}
              </span>
            </div>
            <div class="stage-status" [ngClass]="getStatusClass(getSectionStatus('response'))">
              <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('response'))"></i>
            </div>
            <i class="fa-solid fa-chevron-down toggle-icon"></i>
          </button>

          <div class="stage-body" *ngIf="isSectionExpanded('response') && responseSection">
            <div class="response-card">
              <div class="response-subject" *ngIf="responseSection.subject">
                <label><i class="fa-solid fa-tag"></i> Subject</label>
                <span>{{ responseSection.subject }}</span>
              </div>

              <div class="response-summary" *ngIf="responseSection.summary">
                <label><i class="fa-solid fa-align-left"></i> Summary</label>
                <p>{{ responseSection.summary }}</p>
              </div>

              <div class="response-preview">
                <label><i class="fa-solid fa-eye"></i> Preview</label>
                <div class="preview-content">
                  {{ truncateText(responseSection.plainTextBody, 400) }}
                </div>
              </div>

              <div class="internal-comments" *ngIf="responseSection.internalComments">
                <label><i class="fa-solid fa-sticky-note"></i> Internal Notes</label>
                <p>{{ responseSection.internalComments }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== STAGE 9: Fact Check ==================== -->
      <div class="pipeline-stage stage-factcheck" [class.collapsed]="!isSectionExpanded('factCheck')">
        <div class="stage-connector">
          <div class="connector-line last"></div>
          <div class="connector-dot" [ngClass]="getStatusClass(getSectionStatus('factCheck'))">
            <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('factCheck'))"></i>
          </div>
        </div>

        <div class="stage-content">
          <button class="stage-header" (click)="toggleSection('factCheck')">
            <div class="stage-icon">
              <i class="fa-solid fa-check-double"></i>
            </div>
            <div class="stage-title-area">
              <h3>Fact Check</h3>
              <span class="stage-subtitle" *ngIf="factCheckSection?.complete">
                <ng-container *ngIf="factCheckSection?.passed">
                  {{ factCheckSection?.attemptCount && factCheckSection.attemptCount > 1 ? 'Passed after ' + factCheckSection.attemptCount + ' attempts' : 'All claims verified' }}
                </ng-container>
                <ng-container *ngIf="!factCheckSection?.passed">
                  {{ factCheckIssues.length + ' issue' + (factCheckIssues.length !== 1 ? 's' : '') + ' found' }}
                </ng-container>
              </span>
            </div>
            <!-- Show iteration badge in header when multiple attempts -->
            <span class="iteration-badge" *ngIf="factCheckSection?.attemptCount && factCheckSection.attemptCount > 1">
              <i class="fa-solid fa-rotate"></i> {{ factCheckSection.attemptCount }}
            </span>
            <div class="stage-status" [ngClass]="getStatusClass(getSectionStatus('factCheck'))">
              <i class="fa-solid" [ngClass]="getStatusIcon(getSectionStatus('factCheck'))"></i>
            </div>
            <i class="fa-solid fa-chevron-down toggle-icon"></i>
          </button>

          <div class="stage-body" *ngIf="isSectionExpanded('factCheck') && factCheckSection">
            <div class="factcheck-result" [class.failed]="!factCheckSection.passed">
              <div class="factcheck-badge passed" *ngIf="factCheckSection.passed">
                <i class="fa-solid fa-shield-check"></i>
                <span>All Claims Verified</span>
              </div>
              <div class="factcheck-badge failed" *ngIf="!factCheckSection.passed">
                <i class="fa-solid fa-shield-xmark"></i>
                <span>Issues Found</span>
              </div>

              <!-- Iteration notice when multiple attempts -->
              <div class="iteration-notice" *ngIf="factCheckSection.attemptCount && factCheckSection.attemptCount > 1">
                <div class="iteration-icon">
                  <i class="fa-solid fa-arrows-rotate"></i>
                </div>
                <div class="iteration-details">
                  <span class="iteration-title">Iterative Refinement</span>
                  <span class="iteration-desc">
                    Response was refined {{ factCheckSection.attemptCount }} times between Response Writer and Fact Checker
                  </span>
                </div>
              </div>

              <div class="issues-list" *ngIf="factCheckIssues.length > 0">
                <div class="issue-card" *ngFor="let issue of factCheckIssues"
                     [ngClass]="getIssueSeverityClass(issue.severity)">
                  <div class="issue-header">
                    <i class="fa-solid" [ngClass]="getIssueSeverityIcon(issue.severity)"></i>
                    <span class="issue-severity">{{ issue.severity | titlecase }}</span>
                  </div>
                  <div class="issue-claim">
                    <label>Claim:</label>
                    <span>{{ issue.claim }}</span>
                  </div>
                  <div class="issue-problem">
                    <label>Issue:</label>
                    <span>{{ issue.issue }}</span>
                  </div>
                  <div class="issue-suggestion" *ngIf="issue.suggestion">
                    <label>Suggestion:</label>
                    <span>{{ issue.suggestion }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

  </div>
</ng-container>

<!-- No Payload State -->
<div class="empty-payload" *ngIf="!payload">
  <i class="fa-solid fa-brain"></i>
  <p>No payload data available</p>
</div>
